<template>
  <div
    class="banner-programacao"
    :class="{ 'banner-programacao--dark': darkEnabled }"
  >
    <h5 class="banner-programacao__title">
      <v-icon small class="banner-programacao__icon">fas fa-calendar-alt</v-icon>
      {{ textos.titulo }}
    </h5>
    <p class="banner-programacao__subtitle">{{ textos.subtitulo }}</p>

    <FormSelect
      v-model="tipoProgramacao"
      :options="opcoesTipo"
      :clearable="false"
    />

    <template v-if="isPeriodo">
      <v-row dense>
        <v-col cols="12" sm="6">
          <label class="banner-programacao__label">Data de início</label>
          <FormDatetimePicker
            format="dd/MM/yyyy HH:mm"
            label="Selecione a data de início"
            :clearable="false"
            v-model="inicioPeriodo"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <label class="banner-programacao__label">Data final</label>
          <FormDatetimePicker
            format="dd/MM/yyyy HH:mm"
            label="Selecione a data final"
            :clearable="false"
            v-model="terminoPeriodo"
          />
        </v-col>
      </v-row>
      <p class="banner-programacao__hint">{{ textos.hintPeriodo }}</p>
      <p v-if="vigenciaReferencia" class="banner-programacao__hint">
        {{ vigenciaReferencia }}
      </p>
    </template>

    <template v-else-if="isDiasSemana">
      <label class="banner-programacao__label">Dias de exibição</label>
      <div class="banner-programacao__dias">
        <FormCheckbox
          v-for="dia in diasOpcoes"
          :key="dia.value"
          v-model="diasSemana"
          :val="dia.value"
          :label="dia.text"
        />
      </div>
      <p class="banner-programacao__hint">{{ textos.hintDias }}</p>
      <p class="banner-programacao__hint">
        {{ textos.hintDiasSemanal }}
        <span v-if="vigenciaReferencia">{{ vigenciaReferencia }}</span>
      </p>
    </template>

    <p class="banner-programacao__info">
      <v-icon x-small class="banner-programacao__info-icon"
        >fas fa-info-circle</v-icon
      >
      {{ textoInfo }}
    </p>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FormSelect from "@/components/form/FormSelect";
import FormDatetimePicker from "@/components/form/FormDatetimePicker";
import FormCheckbox from "@/components/form/FormCheckbox";
import {
  DIAS_SEMANA_OPCOES,
  OPCOES_TIPO_PROGRAMACAO,
  TEXTOS_PROGRAMACAO_BANNER,
  TIPO_PROGRAMACAO_BANNER,
  formatarVigenciaCarrosselReferencia,
  montarProgramacaoPayload,
  normalizarProgramacaoBanner
} from "../../utils/carrossel-slide-programacao";

export default {
  name: "CarrosselBannerProgramacao",
  components: {
    FormSelect,
    FormDatetimePicker,
    FormCheckbox
  },
  props: {
    slide: {
      type: Object,
      required: true
    },
    inicioVigencia: {
      type: [String, Date],
      default: null
    },
    fimVigencia: {
      type: [String, Date],
      default: null
    }
  },
  data() {
    return {
      opcoesTipo: OPCOES_TIPO_PROGRAMACAO,
      diasOpcoes: DIAS_SEMANA_OPCOES,
      textos: TEXTOS_PROGRAMACAO_BANNER
    };
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    programacao() {
      return normalizarProgramacaoBanner(this.slide.programacao);
    },
    tipoProgramacao: {
      get() {
        return this.programacao.tipo;
      },
      set(tipo) {
        this.atualizarProgramacao({ tipo });
      }
    },
    inicioPeriodo: {
      get() {
        return this.programacao.inicio || null;
      },
      set(inicio) {
        this.atualizarProgramacao({
          tipo: TIPO_PROGRAMACAO_BANNER.PERIODO,
          inicio,
          termino: this.programacao.termino
        });
      }
    },
    terminoPeriodo: {
      get() {
        return this.programacao.termino || null;
      },
      set(termino) {
        this.atualizarProgramacao({
          tipo: TIPO_PROGRAMACAO_BANNER.PERIODO,
          inicio: this.programacao.inicio,
          termino
        });
      }
    },
    diasSemana: {
      get() {
        return this.programacao.diasSemana || [];
      },
      set(diasSemana) {
        this.atualizarProgramacao({
          tipo: TIPO_PROGRAMACAO_BANNER.DIAS_SEMANA,
          diasSemana
        });
      }
    },
    isPeriodo() {
      return this.programacao.tipo === TIPO_PROGRAMACAO_BANNER.PERIODO;
    },
    isDiasSemana() {
      return this.programacao.tipo === TIPO_PROGRAMACAO_BANNER.DIAS_SEMANA;
    },
    vigenciaReferencia() {
      return formatarVigenciaCarrosselReferencia(
        this.inicioVigencia,
        this.fimVigencia
      );
    },
    textoInfo() {
      if (this.isPeriodo) return this.textos.infoPeriodo;
      if (this.isDiasSemana) return this.textos.infoDias;
      return this.textos.infoSeguir;
    }
  },
  created() {
    this.garantirProgramacaoNoSlide();
  },
  methods: {
    garantirProgramacaoNoSlide() {
      if (this.slide.programacao) return;
      this.$set(this.slide, "programacao", montarProgramacaoPayload());
    },
    atualizarProgramacao(parcial) {
      const atual = normalizarProgramacaoBanner(this.slide.programacao);
      this.$set(
        this.slide,
        "programacao",
        montarProgramacaoPayload({
          ...atual,
          ...parcial
        })
      );
      this.$emit("dirty");
    }
  }
};
</script>

<style scoped>
.banner-programacao {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  text-align: left;
}

.banner-programacao__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #333;
}

.banner-programacao__icon {
  color: var(--primary-color, #5870ef);
}

.banner-programacao__subtitle,
.banner-programacao__hint {
  color: #888;
  font-size: 12px;
  margin: 0 0 12px;
  line-height: 1.4;
}

.banner-programacao__label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 8px;
}

.banner-programacao__dias {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-bottom: 8px;
}

.banner-programacao__info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(88, 112, 239, 0.08);
  color: #555;
  font-size: 12px;
  line-height: 1.4;
}

.banner-programacao__info-icon {
  color: var(--primary-color, #5870ef);
  margin-top: 2px;
}

.banner-programacao--dark {
  border-color: rgba(255, 255, 255, 0.12);
}

.banner-programacao--dark .banner-programacao__title,
.banner-programacao--dark .banner-programacao__label {
  color: rgba(255, 255, 255, 0.87);
}

.banner-programacao--dark .banner-programacao__subtitle,
.banner-programacao--dark .banner-programacao__hint {
  color: rgba(255, 255, 255, 0.65);
}

.banner-programacao--dark .banner-programacao__info {
  background: rgba(88, 112, 239, 0.16);
  color: rgba(255, 255, 255, 0.75);
}
</style>
